<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LocaleLink from '@/components/base/LocaleLink.vue'
import { BUNDLE_TERMS_VERSION, formatVersionDate, hasSnapshot } from '@/legal/termsBundle'
import type { SwitchTerms } from '@/types'

// The terms line or checkbox of a plan switch, between the price block and
// the withdrawal note. An owner on the current terms gets the line; one with
// terms to accept a box, for every contract of the account when the session
// is its holder (can_accept), else for this contract. The box starts unticked,
// and compares from the contract's governing version where one is archived.
const props = defineProps<{
  terms: SwitchTerms | null
  modelValue: boolean
}>()
const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void }>()

const { locale } = useI18n()
const version = computed(() => formatVersionDate(BUNDLE_TERMS_VERSION, locale.value === 'en' ? 'en' : 'de'))
const required = computed(() => props.terms?.needs_acceptance === true)
const declaration = computed(() => (props.terms?.can_accept ? 'common.termSwitch.terms.holder' : 'common.termSwitch.terms.contract'))
const compare = computed(() => {
  const from = props.terms?.governing_version
  return required.value && from && from !== BUNDLE_TERMS_VERSION && hasSnapshot(from) ? { from, to: BUNDLE_TERMS_VERSION } : null
})
const agbLink = hasSnapshot(BUNDLE_TERMS_VERSION)
  ? { to: 'termsVersion', params: { version: BUNDLE_TERMS_VERSION } }
  : { to: 'agb', params: undefined }
</script>

<template>
  <div class="switch-terms">
    <label v-if="required" class="switch-terms-consent">
      <input
        type="checkbox"
        data-test="switch-terms"
        :checked="modelValue"
        @change="emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
      />
      <span>
        <i18n-t :keypath="declaration" tag="span" data-test="switch-terms-text">
          <template #version>{{ version }}</template>
          <template #agb>
            <LocaleLink :to="agbLink.to" :params="agbLink.params" target="_blank" class="switch-terms-link">{{ $t('common.termSwitch.terms.agbLink') }}</LocaleLink>
          </template>
        </i18n-t>
        <template v-if="compare">
          {{ ' ' }}
          <i18n-t keypath="common.termSwitch.terms.compare" tag="span" data-test="switch-terms-compare">
            <template #link>
              <LocaleLink to="termsCompare" :params="compare" target="_blank" class="switch-terms-link">{{ $t('common.termSwitch.terms.compareLink') }}</LocaleLink>
            </template>
          </i18n-t>
        </template>
      </span>
    </label>
    <i18n-t v-else keypath="common.termSwitch.terms.line" tag="p" class="switch-terms-line" data-test="switch-terms-line">
      <template #version>{{ version }}</template>
      <template #agb>
        <LocaleLink :to="agbLink.to" :params="agbLink.params" target="_blank" class="switch-terms-link">{{ $t('common.termSwitch.terms.agbLink') }}</LocaleLink>
      </template>
    </i18n-t>
  </div>
</template>

<style lang="scss" scoped>
.switch-terms {
  font-size: 0.8rem;
  line-height: 1.5;
  color: rgba(200, 218, 255, 0.75);
}

.switch-terms-line {
  margin: 0;
}

.switch-terms-link {
  color: #4fc3f7;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.switch-terms-consent {
  display: flex;
  gap: 8px;
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    accent-color: #00b0ff;
    flex-shrink: 0;
  }
}

.v-theme--lightTheme .switch-terms {
  color: rgba(15, 23, 42, 0.75);
}
</style>
